import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import MarcaTexto from '../components/brand/MarcaTexto';
import AnimatedBrandIcon from '../components/AnimatedBrandIcon';
import { Download, Smartphone, Star, Zap, Bell, Shield, Users, ChevronDown, ChevronUp, ArrowRight, ExternalLink, Clock } from 'lucide-react';

import { API, API_BASE } from '../config/apiBase';


const BENEFITS = [
  { icon: Zap, title: 'Ofertas em tempo real', desc: 'Receba as melhores ofertas assim que aparecem, antes de todo mundo.' },
  { icon: Bell, title: 'Alertas inteligentes', desc: 'Notificacoes personalizadas para os produtos e categorias que voce acompanha.' },
  { icon: Smartphone, title: 'Experiencia otimizada', desc: 'Interface rapida e fluida, feita para voce encontrar ofertas em segundos.' },
  { icon: Users, title: 'Comunidade ativa', desc: 'Faca parte de um grupo que compartilha as melhores oportunidades.' },
  { icon: Shield, title: 'Seguro e confiavel', desc: 'Links verificados, lojas confiaveis e historico de precos transparente.' },
  { icon: Star, title: 'Recursos exclusivos', desc: 'Funcionalidades premium disponiveis apenas no aplicativo.' },
];

const FAQ_ITEMS = [
  { q: 'O app ja esta disponivel?', a: 'Sim! Voce pode baixar agora mesmo clicando no botao de download. A versao atual funciona perfeitamente em dispositivos Android.' },
  { q: 'Funciona em Android?', a: 'Sim. O app e compativel com Android 8.0 ou superior. Basta baixar e instalar.' },
  { q: 'Quando sai na Google Play Store?', a: 'Estamos trabalhando na publicacao oficial na Play Store. Enquanto isso, voce pode baixar diretamente pelo nosso site.' },
  { q: 'Quando sai na Apple App Store?', a: 'A versao para iOS esta em desenvolvimento. Em breve teremos novidades.' },
  { q: 'O download e seguro?', a: 'Totalmente. O aplicativo e distribuido diretamente pela SuperVitrine com verificacao de integridade.' },
  { q: 'Preciso criar conta?', a: 'Voce pode navegar pelas ofertas sem conta. Para favoritos, alertas e recursos avancados, basta criar uma conta gratuita.' },
  { q: 'Posso usar pelo navegador tambem?', a: 'Sim! O SuperVitrine funciona perfeitamente no navegador. O app oferece uma experiencia ainda mais rapida e com notificacoes.' },
];

function FaqItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border-b transition-colors cursor-pointer"
      style={{ borderColor: 'rgba(125,165,255,0.16)' }}
      onClick={() => setOpen(!open)}
      data-testid="faq-item"
    >
      <div className="flex items-center justify-between py-4 px-1">
        <span className="text-sm font-medium pr-4" style={{ color: '#e0e0e0' }}>{item.q}</span>
        {open ? <ChevronUp size={16} style={{ color: 'var(--sv-accent)', flexShrink: 0 }} /> : <ChevronDown size={16} style={{ color: '#8FA0BD', flexShrink: 0 }} />}
      </div>
      {open && (
        <div className="pb-4 px-1 text-sm leading-relaxed" style={{ color: '#A8B6CC' }}>
          {item.a}
        </div>
      )}
    </div>
  );
}

export default function BaixarAppPage() {
  const [searchParams] = useSearchParams();
  const fromSystem = searchParams.get('from') === 'system';
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/app-download/config`)
      .then(r => r.json())
      .then(data => setConfig(data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#071A3A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 32, height: 32, border: '3px solid rgba(125,165,255,0.16)', borderTopColor: 'var(--sv-accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const c = config || {};
  const heroTitle = c.hero_title || 'SuperVitrine no seu bolso';
  const heroSub = c.hero_subtitle || 'Ofertas exclusivas direto no seu celular.';
  const ctaText = c.cta_text || 'Baixar agora';
  const comingSoon = c.coming_soon_text || 'Em breve';
  const noteText = c.note_text || 'Download seguro e gratuito';
  const directUrl = c.direct_download_url || '';
  const directActive = c.direct_download_active;
  const fullDownloadUrl = directUrl.startsWith('http') ? directUrl : `${API_BASE}${directUrl}`;
  const playUrl = c.play_store_url || '';
  const playActive = c.play_store_active && playUrl;
  const appStoreUrl = c.app_store_url || '';
  const appStoreActive = c.app_store_active && appStoreUrl;
  // Desktop (Windows .exe)
  const desktopUrl = c.desktop_download_url || '';
  const desktopActive = c.desktop_available && desktopUrl;
  const fullDesktopUrl = desktopUrl.startsWith('http') ? desktopUrl : `${API_BASE}${desktopUrl}`;
  const handleDesktopDownload = () => { if (desktopActive) window.location.href = fullDesktopUrl; };

  const handleDirectDownload = () => {
    if (!directActive || !directUrl) return;
    window.location.href = fullDownloadUrl;
  };

  return (
    <div style={{ minHeight: '100vh', background: '#071A3A', color: '#fff' }}>
      {/* Subtle grid background */}
      <div style={{ position: 'fixed', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle at 1px 1px, #7DB3FF 1px, transparent 0)', backgroundSize: '40px 40px', pointerEvents: 'none', zIndex: 0 }} />

      {/* Nav */}
      <nav className="relative z-10 flex items-center justify-between px-4 py-3 sm:px-8 sm:py-5 gap-3" style={{ borderBottom: '1px solid rgba(125,165,255,0.16)' }}>
        <Link to="/" className="flex items-center gap-2 no-underline shrink-0" data-testid="download-nav-logo">
          <AnimatedBrandIcon size={28} animate={true} glow={true} />
          <MarcaTexto className="text-xs sm:text-sm font-bold tracking-wide" />
        </Link>
        <div className="flex items-center gap-2">
          {fromSystem ? (
            <Link to="/sistema/aplicativos" className="text-[11px] sm:text-xs px-3 py-1.5 rounded-full no-underline transition-colors hover:bg-white/5" style={{ color: 'var(--sv-accent)', border: '1px solid rgba(var(--sv-accent-rgb), 0.28)' }} data-testid="back-to-system">
              Voltar ao sistema
            </Link>
          ) : (
            <Link to="/" className="text-[11px] sm:text-xs px-3 py-1.5 rounded-full no-underline transition-colors hover:bg-white/5" style={{ color: '#A8B6CC', border: '1px solid rgba(125,165,255,0.16)' }}>
              Voltar ao site
            </Link>
          )}
        </div>
      </nav>

      {/* HERO */}
      <section className="relative z-10 px-5 sm:px-8 pt-16 pb-20 sm:pt-24 sm:pb-28 max-w-5xl mx-auto text-center">
        {/* Glow */}
        <div style={{ position: 'absolute', top: '10%', left: '50%', transform: 'translateX(-50%)', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(var(--sv-accent-rgb), 0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6" style={{ background: 'rgba(var(--sv-accent-rgb), 0.08)', border: '1px solid rgba(var(--sv-accent-rgb), 0.28)' }}>
          <Smartphone size={13} style={{ color: 'var(--sv-accent)' }} />
          <span className="text-xs font-medium" style={{ color: 'var(--sv-accent)' }}>Aplicativo oficial</span>
        </div>

        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black leading-tight mb-5" style={{ fontFamily: "var(--font-display)", letterSpacing: '-0.02em' }}>
          {heroTitle.split(' ').map((word, i) => {
            const gold = ['SuperVitrine', 'bolso', 'celular', 'ofertas', 'app'].some(g => word.toLowerCase().includes(g.toLowerCase()));
            return <span key={i}>{i > 0 ? ' ' : ''}<span style={gold ? { color: 'var(--sv-accent)' } : {}}>{word}</span></span>;
          })}
        </h1>

        <p className="text-sm sm:text-base leading-relaxed max-w-xl mx-auto mb-10" style={{ color: '#A8B6CC' }}>
          {heroSub}
        </p>

        {/* Main CTA */}
        {directActive && directUrl ? (
          <button
            onClick={handleDirectDownload}
            className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full text-sm font-bold transition-all hover:scale-105"
            style={{ background: 'var(--sv-action)', color: 'var(--sv-on-action)', boxShadow: '0 0 30px rgba(var(--sv-accent-rgb), 0.2), 0 4px 15px rgba(0,0,0,0.3)' }}
            data-testid="hero-download-btn"
          >
            <Download size={18} />
            {ctaText}
          </button>
        ) : (
          <div className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full text-sm font-bold" style={{ background: '#0D2C63', color: '#8FA0BD', border: '1px solid rgba(125,165,255,0.16)' }}>
            <Clock size={18} />
            {comingSoon}
          </div>
        )}

        <p className="text-xs mt-4" style={{ color: '#444' }}>{noteText}</p>
        <p id="apk-status-msg" className="text-xs mt-2" style={{ color: 'var(--sv-accent)', display: 'none' }} data-testid="apk-status-msg"></p>

        {/* QR Code - desktop only, when direct download active */}
        {directActive && directUrl && (
          <div className="hidden sm:flex flex-col items-center mt-8" data-testid="qr-code-section">
            <div className="rounded-2xl p-4" style={{ background: '#fff' }}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(fullDownloadUrl)}&bgcolor=ffffff&color=000000&format=svg`}
                alt="QR Code para download"
                width={160}
                height={160}
                style={{ display: 'block' }}
              />
            </div>
            <p className="text-xs mt-3 flex items-center gap-1.5" style={{ color: '#8FA0BD' }}>
              <Smartphone size={12} />
              Escaneie com o celular para baixar
            </p>
          </div>
        )}

        {/* Phone mockup area */}
        <div className="mt-14 flex justify-center">
          <div className="relative" style={{ width: 220, height: 420 }}>
            {/* Phone frame */}
            <div className="absolute inset-0 rounded-3xl" style={{ background: '#0B214A', border: '2px solid rgba(125,165,255,0.16)', boxShadow: '0 0 60px rgba(var(--sv-accent-rgb), 0.05), 0 20px 60px rgba(0,0,0,0.5)' }}>
              {/* Notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 rounded-b-xl" style={{ background: '#071A3A' }} />
              {/* Screen content */}
              <div className="absolute inset-3 top-7 rounded-2xl overflow-hidden" style={{ background: '#071A3A' }}>
                {/* Mini header */}
                <div className="flex items-center gap-2 px-3 py-2" style={{ borderBottom: '1px solid rgba(125,165,255,0.16)' }}>
                  <div className="w-5 h-5 rounded" style={{ background: 'var(--sv-brand-icon-bg)' }}>
                    <span className="flex items-center justify-center h-full" style={{ color: 'var(--sv-brand-icon-fg)', fontSize: 8, fontWeight: 800 }}>S</span>
                  </div>
                  <MarcaTexto style={{ fontSize: 8, fontWeight: 700 }} />
                </div>
                {/* Mini cards */}
                {[1, 2, 3].map(i => (
                  <div key={i} className="mx-2 mt-2 rounded-lg p-2" style={{ background: '#0B214A', border: '1px solid rgba(125,165,255,0.16)' }}>
                    <div className="flex gap-2">
                      <div className="w-10 h-10 rounded" style={{ background: `linear-gradient(135deg, ${i === 1 ? '#3D8BFF' : i === 2 ? '#2563EB' : '#10B981'}20, transparent)`, border: '1px solid rgba(125,165,255,0.16)' }} />
                      <div className="flex-1">
                        <div className="h-2 rounded-full mb-1.5" style={{ background: '#0D2C63', width: '80%' }} />
                        <div className="h-1.5 rounded-full" style={{ background: '#0D2C63', width: '60%' }} />
                        <div className="mt-2 flex items-center gap-1">
                          <span style={{ color: 'var(--sv-accent)', fontSize: 9, fontWeight: 700 }}>R$ {(19.90 * i).toFixed(2).replace('.', ',')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Glow behind phone */}
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(var(--sv-accent-rgb), 0.08) 0%, transparent 70%)', pointerEvents: 'none', zIndex: -1 }} />
          </div>
        </div>
      </section>

      {/* APK INFO CARD */}
      {directActive && (c.app_version || c.app_size) && (
        <section className="relative z-10 px-5 sm:px-8 pb-12 max-w-md mx-auto">
          <div className="rounded-2xl p-5" style={{ background: '#071A3A', border: '1px solid rgba(125,165,255,0.16)' }} data-testid="apk-info-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--sv-brand-icon-bg)' }}>
                <span style={{ color: 'var(--sv-brand-icon-fg)', fontWeight: 800, fontSize: 14 }}>S</span>
              </div>
              <div>
                <p className="text-sm font-bold" style={{ color: '#e0e0e0' }}>SuperVitrine</p>
                <p className="text-[10px]" style={{ color: '#8FA0BD' }}>com.supervitrine.app</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {c.app_version && (
                <div className="text-center p-2 rounded-lg" style={{ background: '#0B214A', border: '1px solid rgba(125,165,255,0.16)' }}>
                  <p className="text-[10px] mb-1" style={{ color: '#8FA0BD' }}>Versao</p>
                  <p className="text-xs font-bold" style={{ color: 'var(--sv-accent)' }}>{c.app_version}</p>
                </div>
              )}
              {c.app_size && (
                <div className="text-center p-2 rounded-lg" style={{ background: '#0B214A', border: '1px solid rgba(125,165,255,0.16)' }}>
                  <p className="text-[10px] mb-1" style={{ color: '#8FA0BD' }}>Tamanho</p>
                  <p className="text-xs font-bold" style={{ color: '#e0e0e0' }}>{c.app_size}</p>
                </div>
              )}
              {c.app_updated_at && (
                <div className="text-center p-2 rounded-lg" style={{ background: '#0B214A', border: '1px solid rgba(125,165,255,0.16)' }}>
                  <p className="text-[10px] mb-1" style={{ color: '#8FA0BD' }}>Atualizado</p>
                  <p className="text-xs font-bold" style={{ color: '#e0e0e0' }}>{c.app_updated_at}</p>
                </div>
              )}
            </div>
            {c.app_changelog && (
              <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(125,165,255,0.16)' }}>
                <p className="text-[10px] font-medium mb-1" style={{ color: '#8FA0BD' }}>Novidades</p>
                <p className="text-xs leading-relaxed" style={{ color: '#8FA0BD' }}>{c.app_changelog}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* DESKTOP DOWNLOAD — destaque (Windows .exe pronto) */}
      {desktopActive && (
        <section className="relative z-10 px-5 sm:px-8 pb-10 max-w-3xl mx-auto" data-testid="desktop-download-section">
          <div className="rounded-2xl p-6 sm:p-8" style={{ background: 'linear-gradient(135deg, rgba(var(--sv-accent-rgb), 0.08) 0%, rgba(var(--sv-accent-rgb), 0.08) 100%)', border: '1px solid rgba(var(--sv-accent-rgb), 0.28)', boxShadow: '0 0 40px rgba(var(--sv-accent-rgb), 0.06)' }}>
            <div className="flex flex-col sm:flex-row items-center gap-5 text-center sm:text-left">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0" style={{ background: 'var(--sv-brand-icon-bg)' }}>
                {/* Windows logo */}
                <svg viewBox="0 0 24 24" width="30" height="30" fill="#FFFFFF"><path d="M3 5.557l7.357-1.002.004 7.097-7.354.042L3 5.557zm7.354 6.913l.006 7.103-7.354-1.011v-6.14l7.348.048zm.892-8.046L21.001 3v8.562l-9.755.077V4.424zm9.758 8.113L21 21.999l-9.755-1.378-.014-7.116 9.783.05z"/></svg>
              </div>
              <div className="flex-1">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full mb-2" style={{ background: 'rgba(var(--sv-accent-rgb), 0.08)', border: '1px solid rgba(var(--sv-accent-rgb), 0.28)' }}>
                  <span className="text-[10px] font-bold" style={{ color: 'var(--sv-accent)' }}>NOVO • v{c.desktop_version || '1.3.0'}</span>
                </div>
                <h3 className="text-lg sm:text-xl font-black" style={{ color: '#fff', fontFamily: 'var(--font-display)' }}>App para Windows</h3>
                <p className="text-xs sm:text-sm mt-1" style={{ color: '#A8B6CC' }}>
                  Instale o SuperVitrine no seu computador. {c.desktop_os || 'Windows 10/11'} · {c.desktop_size || '77 MB'}
                </p>
              </div>
              <button
                onClick={handleDesktopDownload}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold transition-all hover:scale-105 shrink-0"
                style={{ background: 'var(--sv-action)', color: 'var(--sv-on-action)', boxShadow: '0 4px 15px rgba(0,0,0,0.3)' }}
                data-testid="btn-desktop-download"
              >
                <Download size={18} />
                Baixar para Windows
              </button>
            </div>
          </div>
        </section>
      )}

      {/* DOWNLOAD OPTIONS */}
      <section className="relative z-10 px-5 sm:px-8 pb-20 max-w-4xl mx-auto">
        <h2 className="text-base sm:text-lg font-bold text-center mb-8" style={{ color: '#e0e0e0' }}>
          Escolha como baixar
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Direct Download */}
          <div
            className="rounded-2xl p-5 text-center transition-all hover:scale-[1.02]"
            style={{
              background: directActive ? 'linear-gradient(180deg, rgba(var(--sv-accent-rgb), 0.08) 0%, rgba(var(--sv-accent-rgb), 0.08) 100%)' : '#0B214A',
              border: directActive ? '1px solid rgba(var(--sv-accent-rgb), 0.28)' : '1px solid rgba(125,165,255,0.16)',
              boxShadow: directActive ? '0 0 30px rgba(var(--sv-accent-rgb), 0.05)' : 'none',
            }}
            data-testid="card-direct-download"
          >
            <div className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center" style={{ background: directActive ? 'var(--sv-action)' : '#0D2C63' }}>
              <Download size={22} style={{ color: directActive ? '#03230F' : '#6B7C9A' }} />
            </div>
            <h3 className="text-sm font-bold mb-1" style={{ color: directActive ? 'var(--sv-accent)' : 'var(--muted-2)' }}>Download Direto</h3>
            <p className="text-xs mb-4" style={{ color: '#8FA0BD' }}>APK para Android</p>
            {directActive && directUrl ? (
              <button onClick={handleDirectDownload} className="w-full py-2.5 rounded-lg text-xs font-bold transition-colors hover:opacity-90" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="btn-direct-download">
                {ctaText}
              </button>
            ) : (
              <div className="w-full py-2.5 rounded-lg text-xs font-medium" style={{ background: '#0B214A', color: '#444', border: '1px solid rgba(125,165,255,0.16)' }}>
                {comingSoon}
              </div>
            )}
          </div>

          {/* Google Play */}
          <div
            className="rounded-2xl p-5 text-center transition-all hover:scale-[1.02]"
            style={{
              background: playActive ? 'linear-gradient(180deg, #001a0d 0%, #000a08 100%)' : '#0B214A',
              border: playActive ? '1px solid #00381a' : '1px solid rgba(125,165,255,0.16)',
            }}
            data-testid="card-play-store"
          >
            <div className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center" style={{ background: playActive ? '#10B981' : '#0D2C63' }}>
              <svg viewBox="0 0 24 24" width="22" height="22" fill={playActive ? '#03230F' : '#6B7C9A'}>
                <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302L17.7 13.11l-2.302-2.302 2.302-2.302zM5.864 2.658L16.8 8.991l-2.302 2.302L5.864 2.658z"/>
              </svg>
            </div>
            <h3 className="text-sm font-bold mb-1" style={{ color: playActive ? '#10B981' : '#555' }}>Google Play</h3>
            <p className="text-xs mb-4" style={{ color: '#8FA0BD' }}>Android</p>
            {playActive ? (
              <a href={playUrl} target="_blank" rel="noopener noreferrer" className="block w-full py-2.5 rounded-lg text-xs font-bold no-underline transition-colors hover:opacity-90" style={{ background: '#10B981', color: '#071A3A' }} data-testid="btn-play-store">
                Abrir na Play Store
              </a>
            ) : (
              <div className="w-full py-2.5 rounded-lg text-xs font-medium" style={{ background: '#0B214A', color: '#444', border: '1px solid rgba(125,165,255,0.16)' }}>
                {comingSoon}
              </div>
            )}
          </div>

          {/* App Store */}
          <div
            className="rounded-2xl p-5 text-center transition-all hover:scale-[1.02]"
            style={{
              background: appStoreActive ? 'linear-gradient(180deg, #000d1a 0%, #00080a 100%)' : '#0B214A',
              border: appStoreActive ? '1px solid #002a4d' : '1px solid rgba(125,165,255,0.16)',
            }}
            data-testid="card-app-store"
          >
            <div className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center" style={{ background: appStoreActive ? '#3B82F6' : '#0D2C63' }}>
              <svg viewBox="0 0 24 24" width="22" height="22" fill={appStoreActive ? '#03230F' : '#6B7C9A'}>
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
            </div>
            <h3 className="text-sm font-bold mb-1" style={{ color: appStoreActive ? '#3B82F6' : '#555' }}>App Store</h3>
            <p className="text-xs mb-4" style={{ color: '#8FA0BD' }}>iPhone e iPad</p>
            {appStoreActive ? (
              <a href={appStoreUrl} target="_blank" rel="noopener noreferrer" className="block w-full py-2.5 rounded-lg text-xs font-bold no-underline transition-colors hover:opacity-90" style={{ background: '#3B82F6', color: '#fff' }} data-testid="btn-app-store">
                Abrir na App Store
              </a>
            ) : (
              <div className="w-full py-2.5 rounded-lg text-xs font-medium" style={{ background: '#0B214A', color: '#444', border: '1px solid rgba(125,165,255,0.16)' }}>
                {comingSoon}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="relative z-10 px-5 sm:px-8 py-20 max-w-4xl mx-auto" style={{ borderTop: '1px solid rgba(125,165,255,0.16)' }}>
        <h2 className="text-base sm:text-lg font-bold text-center mb-10" style={{ color: '#e0e0e0' }}>
          Por que baixar o <span style={{ color: 'var(--sv-accent)' }}>SuperVitrine</span>?
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {BENEFITS.map((b, i) => (
            <div key={i} className="rounded-xl p-4 transition-colors hover:bg-white/[0.02]" style={{ background: '#071A3A', border: '1px solid #141414' }} data-testid={`benefit-${i}`}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ background: 'rgba(var(--sv-accent-rgb), 0.08)', border: '1px solid rgba(var(--sv-accent-rgb), 0.08)' }}>
                <b.icon size={16} style={{ color: 'var(--sv-accent)' }} />
              </div>
              <h3 className="text-sm font-bold mb-1" style={{ color: '#e0e0e0' }}>{b.title}</h3>
              <p className="text-xs leading-relaxed" style={{ color: '#8FA0BD' }}>{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* INSTALLATION STEPS */}
      <section className="relative z-10 px-5 sm:px-8 py-20 max-w-3xl mx-auto" style={{ borderTop: '1px solid rgba(125,165,255,0.16)' }}>
        <h2 className="text-base sm:text-lg font-bold text-center mb-8" style={{ color: '#e0e0e0' }}>
          Como instalar
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { step: '1', title: 'Baixe o APK', desc: 'Clique no botao de download acima' },
            { step: '2', title: 'Abra o arquivo', desc: 'Toque no APK baixado na notificacao' },
            { step: '3', title: 'Permita a instalacao', desc: 'Ative "Fontes desconhecidas" se solicitado' },
            { step: '4', title: 'Pronto!', desc: 'Abra o app e faca login' },
          ].map(s => (
            <div key={s.step} className="text-center p-4 rounded-xl" style={{ background: '#071A3A', border: '1px solid #141414' }} data-testid={`install-step-${s.step}`}>
              <div className="w-8 h-8 rounded-full mx-auto mb-3 flex items-center justify-center text-xs font-bold" style={{ background: 'rgba(var(--sv-accent-rgb), 0.08)', border: '1px solid rgba(var(--sv-accent-rgb), 0.28)', color: 'var(--sv-accent)' }}>
                {s.step}
              </div>
              <h3 className="text-xs font-bold mb-1" style={{ color: '#e0e0e0' }}>{s.title}</h3>
              <p className="text-[11px]" style={{ color: '#8FA0BD' }}>{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 p-3 rounded-lg text-center" style={{ background: 'rgba(var(--sv-accent-rgb), 0.08)', border: '1px solid rgba(var(--sv-accent-rgb), 0.08)' }}>
          <p className="text-xs" style={{ color: '#A8B6CC' }}>
            <Shield size={12} className="inline mr-1" style={{ color: 'var(--sv-accent)' }} />
            O APK e distribuido diretamente pela SuperVitrine. Download 100% seguro e verificado.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-10 px-5 sm:px-8 py-20 max-w-2xl mx-auto" style={{ borderTop: '1px solid rgba(125,165,255,0.16)' }}>
        <h2 className="text-base sm:text-lg font-bold text-center mb-8" style={{ color: '#e0e0e0' }}>
          Perguntas frequentes
        </h2>
        <div>
          {FAQ_ITEMS.map((item, i) => <FaqItem key={i} item={item} />)}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative z-10 px-5 sm:px-8 py-20 text-center" style={{ borderTop: '1px solid rgba(125,165,255,0.16)' }}>
        <div className="max-w-lg mx-auto">
          <h2 className="text-xl sm:text-2xl font-black mb-3" style={{ fontFamily: "var(--font-display)" }}>
            Pronto para <span style={{ color: 'var(--sv-accent)' }}>economizar</span>?
          </h2>
          <p className="text-sm mb-8" style={{ color: '#8FA0BD' }}>
            Baixe o SuperVitrine agora e tenha acesso as melhores ofertas na palma da sua mao.
          </p>
          {directActive && directUrl ? (
            <button
              onClick={handleDirectDownload}
              className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full text-sm font-bold transition-all hover:scale-105"
              style={{ background: 'var(--sv-action)', color: 'var(--sv-on-action)', boxShadow: '0 0 30px rgba(var(--sv-accent-rgb), 0.2), 0 4px 15px rgba(0,0,0,0.3)' }}
              data-testid="final-download-btn"
            >
              <Download size={18} />
              {ctaText}
            </button>
          ) : (
            <div className="inline-flex items-center gap-2 text-sm" style={{ color: '#444' }}>
              <Clock size={16} />
              Download disponivel em breve
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-5 sm:px-8 py-6 text-center" style={{ borderTop: '1px solid rgba(125,165,255,0.16)' }}>
        <p className="text-xs" style={{ color: '#6B7C9A' }}>SuperVitrine &copy; {new Date().getFullYear()}</p>
      </footer>
    </div>
  );
}
